<template>
  <component
    :is="lucideIcon"
    v-if="newUiEnabled && lucideIcon"
    class="ui-icon"
    :class="[iconName, extraClasses, { 'ui-spin': isSpinning }]"
    :size="iconSize"
    :stroke-width="2.2"
    aria-hidden="true"
  />

  <i v-else :class="fa" />
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";
import { faIconName, lucideFromFa } from "./faToLucide";

const props = withDefaults(
  defineProps<{
    fa: string;
    size?: number;
  }>(),
  {
    size: undefined,
  },
);

const FA_SIZES: Record<string, number> = {
  "fa-xs": 14,
  "fa-sm": 16,
  "fa-lg": 22,
  "fa-2x": 32,
  "fa-3x": 48,
  "fa-4x": 64,
  "fa-5x": 80,
};

const DEFAULT_SIZE = 18;

const FA_STYLE_CLASSES = ["fa", "fas", "far", "fab"];

const newUiEnabled = useNewUiEnabled();

const classNames = computed(() => props.fa.split(/\s+/).filter(Boolean));

const lucideIcon = computed(() => lucideFromFa(props.fa));

// The Font Awesome name stays on the svg, so styles written for it (e.g. `:has(> .fa-save)`) keep working.
const iconName = computed(() => faIconName(props.fa));

const extraClasses = computed(() =>
  classNames.value.filter(
    (className) => !FA_STYLE_CLASSES.includes(className) && !className.startsWith("fa-"),
  ),
);

const isSpinning = computed(() => classNames.value.includes("fa-spin"));

const iconSize = computed(() => {
  if (props.size) {
    return props.size;
  }

  const sizeClass = classNames.value.find((className) => className in FA_SIZES);

  return sizeClass ? FA_SIZES[sizeClass] : DEFAULT_SIZE;
});
</script>
